import { Controller } from "@hotwired/stimulus"
import RequestHelper from "../helpers/request_helper"

const SAVE_DEBOUNCE_MS = 500
const BACKSPACE_KEY_CODE = 8

// The JSON endpoints below are bare `render json:` actions built from `render_to_string(partial:)`
// - nothing negotiates format, so they need only the Accept header, and the "*/*" has to stay or
// the partial lookup narrows to :json. The debounced auto-save is a plain HTML action and needs no
// extra header at all.
export default class extends Controller {
  static get targets() { return ["content"] }

  disconnect() {
    clearTimeout(this.saveTimer)
  }

  // -- Auto-save (debounced, one shared timer across every field in the form) ------------------

  save(event) {
    const form = event.target.closest("form")

    clearTimeout(this.saveTimer)
    this.saveTimer = setTimeout(() => { this.postForm(form) }, SAVE_DEBOUNCE_MS)
  }

  postForm(form) {
    const options = RequestHelper.defaultPatchOptions()
    options.headers = Object.assign({}, options.headers, { "X-Requested-With": "XMLHttpRequest" })
    options.body = new FormData(form)

    fetch(form.getAttribute("action"), options).
      then(response => {
        if (!response.ok) { throw new Error(`Save failed (${response.status})`); }
        window.ltAlert({ message: "Changes saved." })
      }).
      catch(this.logFailedCall)
  }

  // -- Character counting (client-side only; the cap is JS-enforced, there is no native maxlength) -

  updateCount(event) {
    const input = event.target
    // Per-field data (250 on a question, 100 on an option - see
    // app/views/subdomain/questionnaires/_questionnaire.html.haml) with no single controller-root
    // value that could stand in for both, so this stays a direct read (D59's per-item carve-out).
    const maxLength = parseInt(input.dataset.maxlength, 10)
    const len = input.value.length

    if (isNaN(maxLength) || isNaN(len)) { return; }

    const countTarget = input.closest(".input-container").querySelector(".character-count span")
    if (countTarget) { countTarget.textContent = len; }
  }

  preventOverflow(event) {
    const input = event.target
    // Per-field data - see the comment in updateCount() above.
    const maxLength = parseInt(input.dataset.maxlength, 10)
    const len = input.value.length

    if (isNaN(maxLength) || isNaN(len)) { return; }

    if (len >= maxLength && event.keyCode !== BACKSPACE_KEY_CODE) { event.preventDefault(); }
  }

  // -- Questions --------------------------------------------------------------------------------

  addQuestion(event) {
    event.preventDefault()
    const url = event.currentTarget.getAttribute("href")

    this.postJson(url, "POST", { question: { type: event.params.questionType } }).
      then(html => {
        this.replaceContent(html)
        window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" })
      }).
      catch(this.logFailedCall)
  }

  updateQuestion(event) {
    event.preventDefault()
    const url = event.currentTarget.getAttribute("href")
    const type = event.params.questionType

    // Carries over whatever the admin already typed in this row's question field, even if the
    // debounced auto-save above has not caught up with it yet - otherwise switching a question's
    // type would revert its text to whatever was last persisted.
    const questionInput = event.currentTarget.closest(".inner-container").
      querySelector("input[id^='questionnaire_questions_attributes'][id$='question']")
    const question = questionInput ? questionInput.value : ""

    const attrs = { question: { type: type } }
    if (question) { attrs.question.question = question; }

    this.postJson(url, "PATCH", attrs).
      then(html => this.replaceContent(html)).
      catch(this.logFailedCall)
  }

  deleteQuestion(event) {
    event.preventDefault()
    const url = event.currentTarget.getAttribute("href")

    this.postJson(url, "DELETE").
      then(html => this.replaceContent(html)).
      catch(this.logFailedCall)
  }

  // -- Options ----------------------------------------------------------------------------------

  addOption(event) {
    event.preventDefault()
    const url = event.currentTarget.getAttribute("href")
    const value = event.params.optionType === "other" ? "Other..." : ""

    this.postJson(url, "POST", { question_option: { value: value } }).
      then(html => this.replaceContent(html)).
      catch(this.logFailedCall)
  }

  deleteOption(event) {
    event.preventDefault()
    const url = event.currentTarget.getAttribute("href")

    this.postJson(url, "DELETE").
      then(html => this.replaceContent(html)).
      catch(this.logFailedCall)
  }

  // -- Shared -----------------------------------------------------------------------------------

  postJson(url, method, body) {
    const options = method === "POST" ? RequestHelper.defaultPostOptions()
      : method === "PATCH" ? RequestHelper.defaultPatchOptions()
      : RequestHelper.defaultDeleteOptions()

    options.headers = Object.assign({}, options.headers, { "Accept": "application/json, text/plain, */*" })

    if (body) {
      options.headers = Object.assign({}, options.headers, { "Content-Type": "application/json" })
      options.body = JSON.stringify(body)
    }

    return fetch(url, options).
      then(response => {
        if (!response.ok) { throw new Error(`Request failed (${response.status})`); }
        return response.json()
      }).
      then(responseBody => responseBody.html)
  }

  // The swapped-in HTML carries the same `data-new-questionnaire-target="content"` root, so the
  // next replace still resolves - Stimulus rescans a controller's own subtree on every mutation.
  //
  // `html` is our own server-rendered markup, not user input or a third party's - every caller
  // (addQuestion/updateQuestion/deleteQuestion/addOption/deleteOption -> postJson) reads it from
  // `responseBody.html`, which Subdomain::Questionnaires::QuestionsController and
  // Subdomain::Questionnaires::Questions::OptionsController (#create/#update/#destroy) build with
  // `render_to_string(partial: "subdomain/questionnaires/questionnaire", ...)` - the same partial
  // this page itself renders on load.
  replaceContent(html) {
    this.contentTarget.outerHTML = html
    window.ltAlert({ message: "Changes saved." })
  }

  logFailedCall(response) { console.warn(response) }
}
